import { datadogRum } from '@datadog/browser-rum';
import {
  EyeIcon,
  Flex,
  HideEyeIcon,
  JaneLogo,
  Typography,
} from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import { useCallback, useMemo, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useLocation } from 'react-router';

import { ApiRequestError } from '@jane/shared/data-access';

import type { RootLoaderType } from '../../../data-access';
import { useCreateSession } from '../../../data-access';
import { useKioskSpacing, useTypedRouteLoaderData } from '../../../hooks';
import { KioskThemeProvider } from '../../../providers';
import {
  defaultTheme,
  trackKioskAuthenticated,
  trackingPath,
} from '../../../util';

type KioskPasswordFormFields = {
  password: string;
};

export const KioskPassword = () => {
  const location = useLocation();
  const { store } = useTypedRouteLoaderData<RootLoaderType>('root');
  const formMethods = useForm<KioskPasswordFormFields>();
  const { setError } = formMethods;
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { isIpad } = useKioskSpacing();

  const { mutate: createSession } = useCreateSession(undefined, {
    onError: (err: unknown) => {
      if (!(err instanceof ApiRequestError)) {
        return datadogRum.addError(err);
      }
      let userError: string[] | null = null;
      if (err.response.status >= 400 && err.response.status <= 499) {
        userError = ['Wrong password. Please try again.'];
        setError('password', {
          message: 'Wrong password. Please try again.',
        });
      }
      datadogRum.addError(err, { url: err.response.url });
      trackKioskAuthenticated({
        store,
        authDetails: {
          authType: 'authentication screen',
          backendError: [err.message],
          path: trackingPath(location.pathname, location.search),
          success: false,
          userError,
        },
      });
    },
    onSuccess: () => {
      trackKioskAuthenticated({
        store,
        authDetails: {
          authType: 'authentication screen',
          backendError: null,
          path: trackingPath(location.pathname, location.search),
          success: true,
          userError: null,
        },
      });
    },
  });

  const onSubmit: SubmitHandler<KioskPasswordFormFields> = useCallback(
    async ({ password }) => {
      if (store.kiosk_id) {
        createSession({
          id: store.kiosk_id,
          password,
          store_id: store.id,
          storeSlug: store.url_slug,
        });
      }
    },
    [createSession, store.id, store.kiosk_id, store.url_slug]
  );

  const togglePassword = useCallback(() => {
    setPasswordVisible(!passwordVisible);
  }, [passwordVisible]);

  const [PasswordIcon, passwordIconLabel] = useMemo(
    () =>
      passwordVisible
        ? [EyeIcon, 'hide password']
        : [HideEyeIcon, 'show password'],
    [passwordVisible]
  );

  return (
    <KioskThemeProvider
      theme={{ ...defaultTheme, colors: {} }}
      themeId="kiosk-password"
    >
      <JaneLogo size="lg" />
      {store.kiosk_id && (
        <Flex
          alignItems="stretch"
          flexDirection="column"
          maxWidth="360px"
          mt={64}
          id="kiosk-password"
          width="75%"
        >
          <Form.BaseForm
            formMethods={formMethods}
            name="Kiosk Auth"
            onSubmit={onSubmit}
          >
            <Form.TextField
              autoCapitalize="off"
              autoFocus
              clearIcon
              endIcon={
                <PasswordIcon
                  altText={passwordIconLabel}
                  color="primary"
                  onClick={togglePassword}
                />
              }
              label={<Typography variant="body-bold">Password</Typography>}
              name="password"
              pattern={/.*/}
              type={passwordVisible ? 'text' : 'password'}
            />
            <Form.SubmitButton
              full
              label={
                <Typography
                  color="text-info"
                  variant={isIpad ? 'body-bold' : 'section-bold'}
                >
                  Authenticate
                </Typography>
              }
              mt={24}
              size={isIpad ? 'small' : 'large'}
              variant="primary"
            />
          </Form.BaseForm>
        </Flex>
      )}
    </KioskThemeProvider>
  );
};
